/** @format */

import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';

import type { HomrVisualAssets } from '@shared/data-access/interfaces';
import { MediaHomrAssetComponent } from './media-homr-asset/media-homr-asset.component';
import { MediaScene7Component } from './media-scene7/media-scene7.component';
import { MediaVideoComponent } from './media-video/media-video.component';

@Component({
  selector: 'shared-media-component-media',
  imports: [MediaHomrAssetComponent, MediaVideoComponent, MediaScene7Component],
  templateUrl: './media.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MediaComponent {
  // Inputs as signals
  readonly altText = input<string>();
  // default Desktop image used with Scene 7
  readonly image = input<string>();
  // Optional Mobile image used with Scene 7
  readonly mobileImg = input<string>();
  // Optional Gif used with Scene 7
  readonly gif = input<string>();
  // Optional Mobile Gif used with Scene 7
  readonly gifMobile = input<string>();
  // Title used for the title attribute associated with the image
  readonly itemTitle = input<string>();
  // Optional animation used with Scene 7
  readonly animation = input<string>();
  // Optional youtubeId that is used with Media Video Component
  readonly youtubeId = input<string>();
  // Optional image param used at the end of a scene 7 image url for optimization
  readonly imagePreset = input<string>();
  // Optional image param used at the end of a akamai image url for optimization
  readonly imageWidthParam = input<string>();
  // Boolean to determine if you want the image to lazy load or not
  readonly willLazyLoad = input<boolean>(false);
  // Array of the data uploaded to the homR Image Uploader and used with media homr asset component
  readonly visualAssets = input<[HomrVisualAssets]>();
  // Position on page
  readonly imagePagePosition = input<number>(0);
  // Decide if the image will be set as high priority or not
  readonly prioritize = input<boolean>(false);

  // Computed signals
  protected readonly visualAssetEmpty = computed(() => {
    const assets = this.visualAssets();
    return assets ? !this.isVisualAssetsValid(assets) : true;
  });

  protected readonly shouldLazyLoad = computed(() => {
    const position = this.imagePagePosition();
    const lazyLoad = this.willLazyLoad();
    // Disable lazy load for top 4 images
    return Number(position) <= 4 ? false : lazyLoad;
  });

  /**
   * @description
   *  Checks the value of the array passed for Visual Assets and returns true is the properties exist and
   *  are valid for the media-homr-asset component to use.
   *  Returns falsy if the visual assets array passed is empty or has invalid properties.
   * @param - {visualAssets} array - each element could contain at least one object that has visual asset properties inside, and a length of the number of those properties
   * Ex. [{0: {value: 'image', type: 'image', altText: 'some alt text'}, length: 1}]
   */
  public isVisualAssetsValid(visualAssets: [HomrVisualAssets]): boolean {
    // Guard against null/undefined input
    if (!visualAssets || !Array.isArray(visualAssets)) {
      return false;
    }

    for (const va in visualAssets) {
      if (visualAssets[va]?.length) {
        const { length, ...assets } = visualAssets[va];
        if (length > 0) {
          return this.isVisualAssetObjectValid(assets);
        }
      }
    }

    return false;
  }

  private isVisualAssetObjectValid(assets: Omit<HomrVisualAssets, 'length'>) {
    // Guard against null/undefined assets
    if (!assets || typeof assets !== 'object') {
      return false;
    }

    const assetValues = Object.values(assets);

    // Check that ALL assets are valid (not null/undefined, not empty objects)
    const allKeysValid = assetValues.every(
      (asset) => asset && typeof asset === 'object' && Object.keys(asset).length > 0,
    );

    return allKeysValid;
  }
}
